import { useContext, useEffect, useRef, useState, type FormEvent } from "react";

import { ProjectContext } from "../app/projectContext";
import {
  loadRunCreateRequestSchema,
  type LoadRunCreateRequest,
} from "../services/loadDemand";
import type { SldElements } from "../services/sldElements";
import { useStudyDraft } from "../hooks/useStudyDraft";
import { describeValidationIssue } from "../utils/validationMessages";
import { LoadRow } from "./LoadRow";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker, sldElementsOf } from "./SldElementPicker";
import {
  LOAD_STUDY_LABELS,
  buildLoadRunPayload,
  createInitialLoadStudyDraft,
  createLoadRowDraft,
  isBlankLoadRow,
  loadStudyDraftSchema,
  seedRowIdCounter,
  type LoadRowDraft,
  type LoadStudyDraft,
} from "./loadStudyDraft";

type LoadStudyFormProps = {
  disabled?: boolean;
  onSubmit: (payload: LoadRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): the board is
  // picked from it, and a picked board prefills the loads below it (A28 (b)).
  sldElements?: SldElements | null | undefined;
};

// The load and demand study (EOS-02): a schedule of loads with one coincidence
// factor. The form owns the draft; the rows send patches. It validates the
// whole run request, so a message names the row and the field the user sees.
//
// The payload carries no project revision: the page adds the selected revision
// when it calls the API, exactly as the Fault page does.
export function LoadStudyForm({ disabled = false, onSubmit, sldElements }: LoadStudyFormProps) {
  // Tolerant of a missing provider (no project, no revision), like the fault form.
  const revisionId = useContext(ProjectContext)?.activeRevisionId() ?? null;
  // The entries outlive a reload, Back and the sidebar for as long as the tab is open.
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<LoadStudyDraft>({
      module: "load-demand",
      createInitial: createInitialLoadStudyDraft,
      schema: loadStudyDraftSchema,
      revisionId,
    });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [rowToFocus, setRowToFocus] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // A new row is far down a long form, so the cursor goes to it.
  useEffect(() => {
    if (rowToFocus === null) {
      return;
    }
    formRef.current
      ?.querySelector<HTMLInputElement>(`[data-load-row="${rowToFocus}"] input[name$="-code"]`)
      ?.focus();
    setRowToFocus(null);
  }, [rowToFocus]);

  function updateStudy(patch: Partial<LoadStudyDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function updateLoad(id: string, patch: Partial<LoadRowDraft>) {
    setDraft((current) => ({
      ...current,
      loads: current.loads.map((load) => (load.id === id ? { ...load, ...patch } : load)),
    }));
  }

  function addLoad() {
    // A restored draft brings ids the counter of this page load has not handed out yet.
    seedRowIdCounter(draft.loads.map((load) => load.id));
    const load = createLoadRowDraft();
    setDraft((current) => ({ ...current, loads: [...current.loads, load] }));
    setRowToFocus(load.id);
  }

  function removeLoad(id: string) {
    setDraft((current) => ({
      ...current,
      loads: current.loads.filter((load) => load.id !== id),
    }));
  }

  // The board picked from the diagram: while the load list is empty or holds blank rows only,
  // one row per load below the board (directly or through lower boards), its code and rated
  // kW filled; a typed row is never overwritten (A28 (b)).
  function chooseBoard(code: string) {
    const summary = sldElements?.buses.find((bus) => bus.code === code);
    const below = (summary?.downstream_loads ?? [])
      .map((loadCode) =>
        sldElementsOf(sldElements, ["load"]).find((element) => element.code === loadCode),
      )
      .filter((element) => element !== undefined);
    setDraft((current) => {
      if (below.length === 0 || !current.loads.every(isBlankLoadRow)) {
        return { ...current, sldElementCode: code };
      }
      seedRowIdCounter(current.loads.map((load) => load.id));
      const loads = below.map((element) => ({
        ...createLoadRowDraft(),
        code: element.code,
        ratedPowerKw: element.rating ?? "",
      }));
      return { ...current, sldElementCode: code, loads };
    });
  }

  // Empties the form only; a result already on the page stays until "Clear results".
  function clearForm() {
    setValidationError(null);
    clear();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = loadRunCreateRequestSchema.safeParse(buildLoadRunPayload(draft));

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(
        issue ? describeValidationIssue(issue, LOAD_STUDY_LABELS) : "Review the load study inputs.",
      );
      return;
    }

    await onSubmit(parsed.data);
  }

  return (
    <form aria-label="Load study inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice
          onKeep={keepForThisRevision}
          onClear={clearForm}
          disabled={disabled}
        />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>

        <label>
          Study code
          <input
            name="studyCode"
            value={draft.code}
            onChange={(event) => updateStudy({ code: event.target.value })}
          />
        </label>

        <label>
          Study name
          <input
            name="studyName"
            value={draft.name}
            onChange={(event) => updateStudy({ name: event.target.value })}
          />
        </label>

        {sldElementsOf(sldElements, ["bus"]).length > 0 ? (
          <SldElementPicker
            label="Board from the diagram"
            name="sldElementCode"
            value={draft.sldElementCode}
            onChange={chooseBoard}
            categories={["bus"]}
            elements={sldElements}
          />
        ) : null}

        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">Australia / New Zealand (AS/NZS 3000) - reference data pending</option>
          </select>
        </label>
        <p>
          The load calculation itself carries no profile-dependent value; the profile is
          recorded with the run and must match the design basis of the project revision.
        </p>

        <label>
          Coincidence factor
          <input
            inputMode="decimal"
            name="coincidenceFactor"
            value={draft.coincidenceFactor}
            onChange={(event) => updateStudy({ coincidenceFactor: event.target.value })}
          />
        </label>
        <p data-coincidence-hint="true">
          Applied to the whole group, equally to active and reactive demand. Leave blank if
          not established — the result will be marked Review required.
        </p>

        <label>
          Study notes
          <input
            name="studyNotes"
            value={draft.notes}
            onChange={(event) => updateStudy({ notes: event.target.value })}
          />
        </label>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Loads</legend>
        {draft.loads.map((load, index) => (
          <LoadRow
            key={load.id}
            load={load}
            index={index}
            onChange={(patch) => updateLoad(load.id, patch)}
            onRemove={draft.loads.length > 1 ? () => removeLoad(load.id) : undefined}
          />
        ))}
        <button type="button" onClick={addLoad}>
          Add load
        </button>
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Calculate load study
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}
